// BoardReportGenerator.tsx
// Executive-grade board report generator for cybersecurity risk and investment decisions
// Designed for C-Suite presentations with professional charts and executive summary

import React, { useState, useRef } from 'react';
import { getRiskBandLabel } from '../../../utils/bands';
import {
  FileText,
  Download,
  Printer,
  Mail,
  TrendingUp,
  AlertTriangle,
  DollarSign,
  Shield,
  Target,
  BarChart3,
  PieChart,
  Calendar,
  CheckCircle,
  Users,
  Building,
  TrendingDown,
  Globe,
  Loader2
} from 'lucide-react';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import type { ExecutiveSimulationResults, BudgetOptimizationScenario } from '../../../services/ExecutiveRiskRevenueEngine';
import { boardReportGapLabel, type PrismGap } from './boardReportGapLabel';
import { boardReportRiskLevel } from './boardReportRiskLevel';
import { complianceFactsDisplay, type ComplianceFacts } from '../../../shared/components/prismFacts/complianceFacts';

interface OutsidePressuresData {
  overallScore: number;
  riskLevel: string;
  categories: Record<string, {
    description: string;
    factors: Array<{
      id: string;
      name: string;
      currentScore: number;
      impact: string;
      trend: string;
      measurement: string;
    }>;
  }>;
  executiveSummary?: {
    keyInsights: string[];
    riskAlignment: string;
    priorityActions: string[];
  };
}

interface PrismData {
  // Not for display (no compliance score outside SPRS, 2026-10-29); kept in the payload.
  compliance_coverage_pct?: number | null;
  // PRISM #291 facts from /prism-data; absent or not measured → "Not measured".
  compliance_facts?: ComplianceFacts | null;
  pdr_maturity?: { protect?: number; detect?: number; respond?: number; Protect?: number; Detect?: number; Respond?: number };
  evidence_count?: number;
  top_gaps?: PrismGap[];
  total_assets?: number;
  total_countermeasures?: number;
}

interface BoardReportGeneratorProps {
  simulationResults: ExecutiveSimulationResults;
  companyData: {
    name: string;
    industry: string;
    annualRevenue: number;
    currentCyberBudget: number;
  };
  outsidePressures?: OutsidePressuresData;
  prismData?: PrismData | null;
  crsScore?: number | null;
  onClose: () => void;
}

const BoardReportGenerator: React.FC<BoardReportGeneratorProps> = ({
  simulationResults,
  companyData,
  outsidePressures,
  prismData,
  crsScore,
  onClose
}) => {
  const [reportFormat, setReportFormat] = useState<'executive' | 'detailed' | 'presentation'>('executive');
  const [isExporting, setIsExporting] = useState(false);
  const reportRef = useRef<HTMLDivElement>(null);
  const currentDate = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  const formatCurrency = (amount: number): string => {
    if (amount >= 1000000) {
      return `$${(amount / 1000000).toFixed(1)}M`;
    } else if (amount >= 1000) {
      return `$${(amount / 1000).toFixed(0)}K`;
    } else {
      return `$${amount.toFixed(0)}`;
    }
  };

  const formatPercent = (decimal: number): string => {
    return `${(decimal * 100).toFixed(1)}%`;
  };

  const printReport = () => {
    window.print();
  };

  const exportToPDF = async () => {
    if (!reportRef.current || isExporting) return;
    setIsExporting(true);
    try {
      const el = reportRef.current;

      // 1. Resolve CSS variables for html2canvas (it can't read :root vars)
      const computed = getComputedStyle(document.documentElement);
      const varsToInline: Record<string, string> = {
        '--ra-text-primary': computed.getPropertyValue('--ra-text-primary').trim() || '#e6f1ff',
        '--ra-text-secondary': computed.getPropertyValue('--ra-text-secondary').trim() || '#a8b2d1',
        '--ra-cyber-aqua': computed.getPropertyValue('--ra-cyber-aqua').trim() || '#00d9ff',
        '--ra-success': computed.getPropertyValue('--ra-success').trim() || '#00ff88',
        '--ra-danger': computed.getPropertyValue('--ra-danger').trim() || '#ff4444',
        '--ra-warning': computed.getPropertyValue('--ra-warning').trim() || '#ffaa00',
        '--ra-glass-bg': computed.getPropertyValue('--ra-glass-bg').trim() || 'rgba(255,255,255,0.04)',
        '--ra-glass-border': computed.getPropertyValue('--ra-glass-border').trim() || 'rgba(255,255,255,0.1)',
      };
      for (const [k, v] of Object.entries(varsToInline)) {
        el.style.setProperty(k, v);
      }

      // 2. Swap gradient-text elements to solid color (html2canvas can't render -webkit-background-clip: text)
      const gradientEls = el.querySelectorAll<HTMLElement>('[style*="background-clip"], [style*="BackgroundClip"]');
      const origStyles: Array<{ el: HTMLElement; bg: string; clip: string; fill: string }> = [];
      gradientEls.forEach(gel => {
        if (gel.style.webkitTextFillColor === 'transparent') {
          origStyles.push({
            el: gel,
            bg: gel.style.background,
            clip: gel.style.webkitBackgroundClip,
            fill: gel.style.webkitTextFillColor,
          });
          gel.style.background = 'none';
          gel.style.webkitBackgroundClip = '';
          gel.style.webkitTextFillColor = '';
          gel.style.color = '#00d9ff';
        }
      });

      const canvas = await html2canvas(el, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#0a192f'
      });

      // 3. Restore original styles
      origStyles.forEach(({ el: gel, bg, clip, fill }) => {
        gel.style.background = bg;
        gel.style.webkitBackgroundClip = clip;
        gel.style.webkitTextFillColor = fill;
        gel.style.color = '';
      });
      for (const k of Object.keys(varsToInline)) {
        el.style.removeProperty(k);
      }
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pdfWidth - 20; // 10mm margins
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 10; // top margin

      pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
      heightLeft -= (pdfHeight - 20);

      while (heightLeft > 0) {
        position = -(pdfHeight - 20) * (Math.ceil((imgHeight - heightLeft) / (pdfHeight - 20)));
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 10, position + 10, imgWidth, imgHeight);
        heightLeft -= (pdfHeight - 20);
      }

      const filename = `${companyData.name.replace(/\s+/g, '-')}-Risk-Assessment-${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(filename);
    } catch (error) {
      console.error('PDF export failed:', error);
    } finally {
      setIsExporting(false);
    }
  };

  const emailReport = () => {
    const subject = `Cybersecurity Risk Assessment - ${companyData.name}`;
    const body = `Please find attached the executive cybersecurity risk assessment and investment recommendations for ${companyData.name}.\n\nNote: Please export the report as PDF first, then attach it to this email.`;
    window.open(`mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`);
  };

  // The overall risk level badge is the CRS (consistent with the Intelligence page), or
  // "Not measured". It used to fall back to loss / revenue, which read "Low" when revenue was unmeasured.
  const riskLevel = boardReportRiskLevel(crsScore);

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      backdropFilter: 'blur(4px)',
      zIndex: 50,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '16px'
    }}>
      <div style={{
        background: 'linear-gradient(135deg, var(--ra-navy) 0%, var(--ra-deep-blue) 100%)',
        maxWidth: '1200px',
        maxHeight: '90vh',
        overflowY: 'auto',
        borderRadius: '16px',
        boxShadow: '0 25px 50px rgba(0, 0, 0, 0.25)',
        border: '1px solid rgba(255, 255, 255, 0.1)'
      }}>
        
        {/* Header Controls */}
        <div style={{
          position: 'sticky',
          top: 0,
          background: 'rgba(10, 25, 47, 0.95)',
          backdropFilter: 'blur(10px)',
          borderBottom: '1px solid var(--ra-glass-border)',
          padding: '20px 24px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          zIndex: 10,
          borderRadius: '16px 16px 0 0'
        }}>
          <h2 style={{
            fontSize: '24px',
            fontWeight: '700',
            color: 'var(--ra-text-primary)',
            margin: 0
          }}>Board Report Generator</h2>
          
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <select
              value={reportFormat}
              onChange={(e) => setReportFormat(e.target.value as any)}
              aria-label="Report format"
              style={{
                border: '1px solid var(--ra-glass-border)',
                borderRadius: '8px',
                padding: '6px 12px',
                fontSize: '14px',
                background: 'var(--ra-glass-bg)',
                color: 'var(--ra-text-primary)',
                outline: 'none'
              }}
            >
              <option value="executive">Executive Summary</option>
              <option value="detailed">Detailed Analysis</option>
              <option value="presentation">Presentation Format</option>
            </select>
            
            <button
              onClick={printReport}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                background: 'linear-gradient(135deg, var(--ra-cyber-aqua) 0%, #0066ff 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(0, 217, 255, 0.3)'
              }}
            >
              <Printer aria-hidden="true" size={16} />
              Print
            </button>
            
            <button
              onClick={exportToPDF}
              disabled={isExporting}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                background: isExporting
                  ? 'linear-gradient(135deg, var(--ra-text-secondary) 0%, #a8b2d1 100%)'
                  : 'linear-gradient(135deg, var(--ra-success) 0%, var(--ra-success) 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: isExporting ? 'wait' : 'pointer',
                boxShadow: '0 2px 8px rgba(0, 255, 136, 0.3)'
              }}
            >
              {isExporting ? <Loader2 aria-hidden="true" size={16} className="animate-spin" /> : <Download aria-hidden="true" size={16} />}
              {isExporting ? 'Exporting...' : 'PDF'}
            </button>
            
            <button
              onClick={emailReport}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                background: 'linear-gradient(135deg, #0066ff 0%, var(--ra-cyber-aqua) 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                padding: '8px 16px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(139, 92, 246, 0.3)'
              }}
            >
              <Mail aria-hidden="true" size={16} />
              Email
            </button>
            
            <button
              onClick={onClose}
              style={{
                color: 'var(--ra-text-secondary)',
                background: 'none',
                border: 'none',
                padding: '8px',
                fontSize: '18px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* Report Content */}
        <div ref={reportRef} style={{
          padding: '32px',
          background: 'var(--ra-glass-bg)',
          color: 'var(--ra-text-primary)',
          margin: '16px',
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
          backdropFilter: 'blur(10px)',
          border: '1px solid var(--ra-glass-border)'
        }}>
          
          {/* Company Header */}
          <div style={{
            textAlign: 'center',
            marginBottom: '40px',
            paddingBottom: '24px',
            borderBottom: '2px solid rgba(99, 102, 241, 0.1)'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '16px',
              marginBottom: '24px'
            }}>
              <div style={{
                padding: '16px',
                background: 'linear-gradient(135deg, var(--ra-navy) 0%, var(--ra-deep-blue) 100%)',
                borderRadius: '16px',
                boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)'
              }}>
                <Shield aria-hidden="true" size={40} color="white" />
              </div>
              <div style={{ textAlign: 'left' }}>
                <h1 style={{
                  fontSize: '36px',
                  fontWeight: '800',
                  color: 'var(--ra-text-primary)',
                  margin: 0,
                  background: 'linear-gradient(135deg, var(--ra-cyber-aqua) 0%, #0066ff 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>Cybersecurity Risk Assessment</h1>
                <p style={{
                  fontSize: '18px',
                  color: 'var(--ra-text-secondary)',
                  margin: 0,
                  fontWeight: '500'
                }}>Executive Investment Recommendation</p>
              </div>
            </div>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: '20px',
              marginTop: '32px'
            }}>
              <div style={{
                background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.1) 0%, rgba(139, 92, 246, 0.1) 100%)',
                padding: '20px',
                borderRadius: '16px',
                border: '1px solid rgba(99, 102, 241, 0.2)',
                boxShadow: '0 4px 16px rgba(99, 102, 241, 0.1)',
                textAlign: 'center'
              }}>
                <Building aria-hidden="true" size={24} color="var(--ra-cyber-aqua)" style={{ margin: '0 auto 8px' }} />
                <div style={{
                  fontWeight: '700',
                  color: 'var(--ra-text-primary)',
                  fontSize: '18px',
                  marginBottom: '4px'
                }}>{companyData.name}</div>
                <div style={{
                  fontSize: '14px',
                  color: 'var(--ra-cyber-aqua)',
                  fontWeight: '600'
                }}>{companyData.industry}</div>
              </div>
              
              <div style={{
                background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
                padding: '20px',
                borderRadius: '16px',
                border: '1px solid rgba(16, 185, 129, 0.2)',
                boxShadow: '0 4px 16px rgba(16, 185, 129, 0.1)',
                textAlign: 'center'
              }}>
                <DollarSign aria-hidden="true" size={24} color="var(--ra-success)" style={{ margin: '0 auto 8px' }} />
                <div style={{
                  fontWeight: '700',
                  color: 'var(--ra-text-primary)',
                  fontSize: '18px',
                  marginBottom: '4px'
                }}>{formatCurrency(companyData.annualRevenue)}</div>
                <div style={{
                  fontSize: '14px',
                  color: 'var(--ra-success)',
                  fontWeight: '600'
                }}>Annual Revenue</div>
              </div>
              
              <div style={{
                background: 'linear-gradient(135deg, rgba(251, 191, 36, 0.1) 0%, rgba(245, 158, 11, 0.1) 100%)',
                padding: '20px',
                borderRadius: '16px',
                border: '1px solid rgba(251, 191, 36, 0.2)',
                boxShadow: '0 4px 16px rgba(251, 191, 36, 0.1)',
                textAlign: 'center'
              }}>
                <Calendar aria-hidden="true" size={24} color="var(--ra-warning)" style={{ margin: '0 auto 8px' }} />
                <div style={{
                  fontWeight: '700',
                  color: 'var(--ra-text-primary)',
                  fontSize: '18px',
                  marginBottom: '4px'
                }}>{currentDate}</div>
                <div style={{
                  fontSize: '14px',
                  color: 'var(--ra-warning)',
                  fontWeight: '600'
                }}>Report Date</div>
              </div>
              
              <div style={{
                background: `linear-gradient(135deg, ${riskLevel.color}18 0%, ${riskLevel.color}10 100%)`,
                padding: '20px',
                borderRadius: '16px',
                border: `1px solid ${riskLevel.color}33`,
                boxShadow: `0 4px 16px ${riskLevel.color}1a`,
                textAlign: 'center'
              }}>
                <div style={{ fontSize: '24px', margin: '0 auto 8px' }}>{riskLevel.icon}</div>
                <div style={{
                  fontWeight: '700',
                  color: riskLevel.color,
                  fontSize: '18px',
                  marginBottom: '4px'
                }}>{riskLevel.level}</div>
                <div style={{
                  fontSize: '14px',
                  color: riskLevel.color,
                  fontWeight: '600'
                }}>Current Status</div>
              </div>
            </div>
          </div>

          {/* Executive Summary */}
          <div style={{ marginBottom: '48px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '32px'
            }}>
              <div style={{
                padding: '12px',
                background: 'linear-gradient(135deg, var(--ra-cyber-aqua) 0%, #0066ff 100%)',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0, 217, 255, 0.3)'
              }}>
                <FileText aria-hidden="true" size={24} color="white" />
              </div>
              <h2 style={{
                fontSize: '28px',
                fontWeight: '800',
                color: 'var(--ra-text-primary)',
                margin: 0,
                background: 'linear-gradient(135deg, var(--ra-cyber-aqua) 0%, #0066ff 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>Executive Summary</h2>
            </div>
            
            {/* Risk Exposure Card */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.05) 0%, rgba(220, 38, 38, 0.05) 100%)',
              border: '2px solid rgba(239, 68, 68, 0.2)',
              borderRadius: '20px',
              padding: '32px',
              marginBottom: '24px',
              boxShadow: '0 8px 32px rgba(239, 68, 68, 0.1)',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <div style={{
                position: 'absolute',
                top: '-50px',
                right: '-50px',
                width: '120px',
                height: '120px',
                background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%)',
                borderRadius: '50%',
                pointerEvents: 'none'
              }} />
              
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '24px'
              }}>
                <AlertTriangle aria-hidden="true" size={28} color="var(--ra-danger)" />
                <h3 style={{
                  fontSize: '24px',
                  fontWeight: '700',
                  color: 'var(--ra-danger)',
                  margin: 0
                }}>Current Risk Exposure</h3>
              </div>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '24px'
              }}>
                <div style={{
                  background: 'rgba(255, 255, 255, 0.04)',
                  padding: '24px',
                  borderRadius: '16px',
                  border: '1px solid rgba(239, 68, 68, 0.1)',
                  backdropFilter: 'blur(10px)'
                }}>
                  <div style={{
                    fontSize: '14px',
                    color: 'var(--ra-danger)',
                    marginBottom: '8px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>Annualized Loss Exposure (ALE)</div>
                  <div style={{
                    fontSize: '32px',
                    fontWeight: '800',
                    color: 'var(--ra-danger)',
                    marginBottom: '4px'
                  }}>
                    {formatCurrency(simulationResults.currentState.expectedAnnualLoss)}
                  </div>
                  <div style={{
                    fontSize: '16px',
                    color: 'var(--ra-danger)',
                    fontWeight: '500'
                  }}>
                    ({formatPercent(simulationResults.currentState.expectedAnnualLoss / companyData.annualRevenue)} of revenue)
                  </div>
                </div>
                
                <div style={{
                  background: 'rgba(255, 255, 255, 0.04)',
                  padding: '24px',
                  borderRadius: '16px',
                  border: '1px solid rgba(239, 68, 68, 0.1)',
                  backdropFilter: 'blur(10px)'
                }}>
                  <div style={{
                    fontSize: '14px',
                    color: 'var(--ra-danger)',
                    marginBottom: '8px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>Value at Risk (95th Percentile)</div>
                  <div style={{
                    fontSize: '32px',
                    fontWeight: '800',
                    color: 'var(--ra-danger)',
                    marginBottom: '4px'
                  }}>
                    {formatCurrency(simulationResults.currentState.percentile95Loss)}
                  </div>
                  <div style={{
                    fontSize: '16px',
                    color: 'var(--ra-danger)',
                    fontWeight: '500'
                  }}>
                    ({formatPercent(simulationResults.currentState.percentile95Loss / companyData.annualRevenue)} of revenue)
                  </div>
                </div>
              </div>
            </div>
            
            {/* Investment Recommendation Card */}
            <div style={{
              background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.05) 0%, rgba(22, 163, 74, 0.05) 100%)',
              border: '2px solid rgba(34, 197, 94, 0.2)',
              borderRadius: '20px',
              padding: '32px',
              marginBottom: '24px',
              boxShadow: '0 8px 32px rgba(34, 197, 94, 0.1)',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <div style={{
                position: 'absolute',
                top: '-50px',
                right: '-50px',
                width: '120px',
                height: '120px',
                background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(22, 163, 74, 0.1) 100%)',
                borderRadius: '50%',
                pointerEvents: 'none'
              }} />
              
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '24px'
              }}>
                <TrendingUp aria-hidden="true" size={28} color="var(--ra-success)" />
                <h3 style={{
                  fontSize: '24px',
                  fontWeight: '700',
                  color: 'var(--ra-success)',
                  margin: 0
                }}>Recommended Strategic Investment</h3>
              </div>
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '24px'
              }}>
                <div style={{
                  background: 'rgba(255, 255, 255, 0.04)',
                  padding: '24px',
                  borderRadius: '16px',
                  border: '1px solid rgba(34, 197, 94, 0.1)',
                  backdropFilter: 'blur(10px)',
                  textAlign: 'center'
                }}>
                  <div style={{
                    fontSize: '14px',
                    color: 'var(--ra-success)',
                    marginBottom: '8px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>Recommended Total Budget</div>
                  <div style={{
                    fontSize: '28px',
                    fontWeight: '800',
                    color: 'var(--ra-success)'
                  }}>
                    {formatCurrency(simulationResults.recommendedScenario.proposedBudget)}
                  </div>
                  <div style={{
                    fontSize: '12px',
                    color: simulationResults.recommendedScenario.budgetIncrease >= 0 ? 'var(--ra-success)' : 'var(--ra-warning)',
                    marginTop: '4px',
                    fontWeight: '500'
                  }}>
                    {simulationResults.recommendedScenario.budgetIncrease >= 0
                      ? `(+${formatCurrency(simulationResults.recommendedScenario.budgetIncrease)} increase)`
                      : `(${formatCurrency(Math.abs(simulationResults.recommendedScenario.budgetIncrease))} savings)`}
                  </div>
                </div>
                
                <div style={{
                  background: 'rgba(255, 255, 255, 0.04)',
                  padding: '24px',
                  borderRadius: '16px',
                  border: '1px solid rgba(34, 197, 94, 0.1)',
                  backdropFilter: 'blur(10px)',
                  textAlign: 'center'
                }}>
                  <div style={{
                    fontSize: '14px',
                    color: 'var(--ra-success)',
                    marginBottom: '8px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>Projected Annual ROI</div>
                  <div style={{
                    fontSize: '28px',
                    fontWeight: '800',
                    color: 'var(--ra-success)'
                  }}>
                    {formatPercent(simulationResults.recommendedScenario.projectedROI)}
                  </div>
                </div>
                
                <div style={{
                  background: 'rgba(255, 255, 255, 0.04)',
                  padding: '24px',
                  borderRadius: '16px',
                  border: '1px solid rgba(34, 197, 94, 0.1)',
                  backdropFilter: 'blur(10px)',
                  textAlign: 'center'
                }}>
                  <div style={{
                    fontSize: '14px',
                    color: 'var(--ra-success)',
                    marginBottom: '8px',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>Payback Period</div>
                  <div style={{
                    fontSize: '28px',
                    fontWeight: '800',
                    color: 'var(--ra-success)'
                  }}>
                    {simulationResults.recommendedScenario.paybackPeriod.toFixed(1)} months
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* External Threat Landscape - detailed only */}
          {reportFormat === 'detailed' && outsidePressures && (
            <div style={{ marginBottom: '48px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '32px'
              }}>
                <div style={{
                  padding: '12px',
                  background: 'linear-gradient(135deg, var(--ra-danger) 0%, var(--ra-danger) 100%)',
                  borderRadius: '12px',
                  boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)'
                }}>
                  <Shield aria-hidden="true" size={24} color="white" />
                </div>
                <h2 style={{
                  fontSize: '28px',
                  fontWeight: '800',
                  color: 'var(--ra-text-primary)',
                  margin: 0,
                  background: 'linear-gradient(135deg, var(--ra-danger) 0%, var(--ra-danger) 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>External Threat Landscape</h2>
              </div>
              
              {/* Overall External Risk Card */}
              <div style={{
                background: 'linear-gradient(135deg, rgba(251, 146, 60, 0.05) 0%, rgba(234, 88, 12, 0.05) 100%)',
                border: '2px solid rgba(251, 146, 60, 0.2)',
                borderRadius: '20px',
                padding: '32px',
                marginBottom: '24px',
                boxShadow: '0 8px 32px rgba(251, 146, 60, 0.1)',
                position: 'relative',
                overflow: 'hidden'
              }}>
                <div style={{
                  position: 'absolute',
                  top: '-50px',
                  right: '-50px',
                  width: '120px',
                  height: '120px',
                  background: 'linear-gradient(135deg, rgba(251, 146, 60, 0.1) 0%, rgba(234, 88, 12, 0.1) 100%)',
                  borderRadius: '50%',
                  pointerEvents: 'none'
                }} />
                
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  marginBottom: '24px'
                }}>
                  <Globe aria-hidden="true" size={28} color="var(--ra-warning)" />
                  <h3 style={{
                    fontSize: '24px',
                    fontWeight: '700',
                    color: 'var(--ra-warning)',
                    margin: 0
                  }}>Current External Risk Environment</h3>
                </div>
                
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                  gap: '24px'
                }}>
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.04)',
                    padding: '24px',
                    borderRadius: '16px',
                    border: '1px solid rgba(251, 146, 60, 0.1)',
                    backdropFilter: 'blur(10px)',
                    textAlign: 'center'
                  }}>
                    <div style={{
                      fontSize: '14px',
                      color: 'var(--ra-warning)',
                      marginBottom: '8px',
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>Overall External Pressure Level</div>
                    <div style={{
                      fontSize: '36px',
                      fontWeight: '800',
                      color: 'var(--ra-warning)',
                      marginBottom: '4px'
                    }}>
                      {getRiskBandLabel(outsidePressures.overallScore)}
                    </div>
                    <div style={{
                      fontSize: '16px',
                      color: 'var(--ra-warning)',
                      fontWeight: '600'
                    }}>
                      {outsidePressures.riskLevel} External Risk
                    </div>
                  </div>
                  
                  <div style={{
                    background: 'rgba(255, 255, 255, 0.04)',
                    padding: '24px',
                    borderRadius: '16px',
                    border: '1px solid rgba(251, 146, 60, 0.1)',
                    backdropFilter: 'blur(10px)',
                    textAlign: 'center'
                  }}>
                    <div style={{
                      fontSize: '14px',
                      color: 'var(--ra-warning)',
                      marginBottom: '8px',
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>Risk Environment Status</div>
                    <div style={{
                      fontSize: '18px',
                      fontWeight: '700',
                      color: 'var(--ra-warning)'
                    }}>
                      {outsidePressures.overallScore >= 80 ? 'Critical - Immediate Action Required' :
                       outsidePressures.overallScore >= 60 ? 'Elevated - Monitor Closely' :
                       'Moderate - Standard Vigilance'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Critical External Risk Categories */}
              {outsidePressures.categories && (
                <div style={{
                  background: 'var(--ra-glass-bg)',
                  border: '2px solid rgba(148, 163, 184, 0.2)',
                  borderRadius: '20px',
                  padding: '32px',
                  marginBottom: '24px',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.05)',
                  backdropFilter: 'blur(10px)'
                }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '24px'
                  }}>
                    <AlertTriangle aria-hidden="true" size={24} color="var(--ra-cyber-aqua)" />
                    <h3 style={{
                      fontSize: '22px',
                      fontWeight: '700',
                      color: 'var(--ra-text-primary)',
                      margin: 0
                    }}>Critical External Risk Categories</h3>
                  </div>
                  
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                    gap: '20px'
                  }}>
                    {Object.entries(outsidePressures.categories)
                      .slice(0, 4) // Show top 4 categories for board report
                      .map(([categoryName, category]) => {
                        const highestRiskFactor = category.factors?.reduce((prev, current) => 
                          (prev.currentScore > current.currentScore) ? prev : current
                        );
                        
                        return (
                          <div key={categoryName} style={{
                            background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(139, 92, 246, 0.05) 100%)',
                            border: '1px solid rgba(99, 102, 241, 0.2)',
                            borderRadius: '16px',
                            padding: '20px',
                            boxShadow: '0 4px 16px rgba(99, 102, 241, 0.1)'
                          }}>
                            <div style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              marginBottom: '12px'
                            }}>
                              <h4 style={{
                                fontWeight: '700',
                                color: 'var(--ra-text-primary)',
                                fontSize: '16px',
                                margin: 0
                              }}>{categoryName}</h4>
                              {highestRiskFactor && (
                                <span style={{
                                  padding: '4px 12px',
                                  borderRadius: '20px',
                                  fontSize: '12px',
                                  fontWeight: '600',
                                  background: highestRiskFactor.currentScore >= 70 ? 'rgba(255, 68, 68, 0.15)' :
                                              highestRiskFactor.currentScore >= 50 ? 'rgba(255, 165, 0, 0.15)' :
                                              'rgba(255, 165, 0, 0.08)',
                                  color: highestRiskFactor.currentScore >= 70 ? 'var(--ra-danger)' :
                                         highestRiskFactor.currentScore >= 50 ? 'var(--ra-warning)' :
                                         'var(--ra-warning)',
                                  border: highestRiskFactor.currentScore >= 70 ? '1px solid rgba(255, 68, 68, 0.3)' :
                                          highestRiskFactor.currentScore >= 50 ? '1px solid rgba(255, 165, 0, 0.3)' :
                                          '1px solid rgba(255, 165, 0, 0.2)'
                                }}>
                                  {highestRiskFactor.currentScore}% Risk
                                </span>
                              )}
                            </div>
                            <p style={{
                              fontSize: '14px',
                              color: 'var(--ra-text-secondary)',
                              marginBottom: '12px',
                              lineHeight: '1.4'
                            }}>{category.description}</p>
                            {highestRiskFactor && (
                              <div style={{
                                fontSize: '12px',
                                color: '#a8b2d1',
                                background: 'rgba(255, 255, 255, 0.04)',
                                padding: '8px',
                                borderRadius: '8px'
                              }}>
                                <div style={{ marginBottom: '4px' }}>
                                  <strong>Primary Concern:</strong> {highestRiskFactor.name}
                                </div>
                                <div>
                                  <strong>Trend:</strong> {highestRiskFactor.trend}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                  </div>
                </div>
              )}

              {/* Executive Risk Assessment */}
              {outsidePressures.executiveSummary && (
                <div style={{
                  background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(37, 99, 235, 0.05) 100%)',
                  border: '2px solid rgba(59, 130, 246, 0.2)',
                  borderRadius: '20px',
                  padding: '32px',
                  boxShadow: '0 8px 32px rgba(59, 130, 246, 0.1)',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: '-50px',
                    right: '-50px',
                    width: '120px',
                    height: '120px',
                    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(37, 99, 235, 0.1) 100%)',
                    borderRadius: '50%',
                    pointerEvents: 'none'
                  }} />
                  
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    marginBottom: '24px'
                  }}>
                    <Target aria-hidden="true" size={28} color="var(--ra-cyber-aqua)" />
                    <h3 style={{
                      fontSize: '24px',
                      fontWeight: '700',
                      color: 'var(--ra-cyber-aqua)',
                      margin: 0
                    }}>External Risk Assessment</h3>
                  </div>
                  
                  <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                    gap: '24px'
                  }}>
                    {outsidePressures.executiveSummary.keyInsights && (
                      <div style={{
                        background: 'rgba(255, 255, 255, 0.04)',
                        padding: '24px',
                        borderRadius: '16px',
                        border: '1px solid rgba(59, 130, 246, 0.1)',
                        backdropFilter: 'blur(10px)'
                      }}>
                        <h4 style={{
                          fontWeight: '700',
                          color: '#0066ff',
                          fontSize: '16px',
                          marginBottom: '16px'
                        }}>Key External Risk Insights</h4>
                        <ul style={{
                          listStyle: 'none',
                          padding: 0,
                          margin: 0,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px'
                        }}>
                          {outsidePressures.executiveSummary.keyInsights.slice(0, 3).map((insight, idx) => (
                            <li key={idx} style={{
                              fontSize: '14px',
                              color: 'var(--ra-cyber-aqua)',
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: '8px'
                            }}>
                              <span style={{
                                width: '6px',
                                height: '6px',
                                borderRadius: '50%',
                                background: 'var(--ra-cyber-aqua)',
                                marginTop: '6px',
                                flexShrink: 0
                              }} />
                              {insight}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    
                    {outsidePressures.executiveSummary.riskAlignment && (
                      <div style={{
                        background: 'rgba(255, 255, 255, 0.04)',
                        padding: '24px',
                        borderRadius: '16px',
                        border: '1px solid rgba(59, 130, 246, 0.1)',
                        backdropFilter: 'blur(10px)'
                      }}>
                        <h4 style={{
                          fontWeight: '700',
                          color: '#0066ff',
                          fontSize: '16px',
                          marginBottom: '16px'
                        }}>Risk Alignment Analysis</h4>
                        <p style={{
                          fontSize: '14px',
                          color: 'var(--ra-cyber-aqua)',
                          lineHeight: '1.5',
                          margin: 0
                        }}>{outsidePressures.executiveSummary.riskAlignment}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Technical Security Posture (PRISM) - Phase 5 */}
          {prismData && (
            <div style={{ marginBottom: '48px' }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '32px'
              }}>
                <div style={{
                  padding: '12px',
                  background: 'linear-gradient(135deg, var(--ra-cyber-aqua) 0%, #0066ff 100%)',
                  borderRadius: '12px',
                  boxShadow: '0 4px 12px rgba(99, 102, 241, 0.3)'
                }}>
                  <Shield aria-hidden="true" size={24} color="white" />
                </div>
                <h2 style={{
                  fontSize: '28px',
                  fontWeight: '800',
                  color: 'var(--ra-text-primary)',
                  margin: 0,
                  background: 'linear-gradient(135deg, var(--ra-cyber-aqua) 0%, #0066ff 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>Technical Security Posture</h2>
              </div>

              <div style={{
                background: 'linear-gradient(135deg, rgba(99, 102, 241, 0.05) 0%, rgba(79, 70, 229, 0.05) 100%)',
                border: '2px solid rgba(99, 102, 241, 0.2)',
                borderRadius: '20px',
                padding: '32px',
                boxShadow: '0 8px 32px rgba(99, 102, 241, 0.1)',
              }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '24px', marginBottom: '28px' }}>
                  {/* Compliance facts (2026-10-29: no compliance % outside SPRS). Used to read
                      "Compliance Coverage {pct ?? 0}%", so a missing value printed 0%. Now: open
                      gaps (critical where known) and % of applicable controls evaluated, or
                      "Not measured" when the facts are absent or old-shape. */}
                  {(() => {
                    const facts = complianceFactsDisplay(prismData.compliance_facts);
                    return (
                      <div style={{ textAlign: 'center' }} data-testid="board-report-compliance-facts">
                        <div style={{ fontSize: facts.measured ? '36px' : '24px', fontWeight: '800', color: 'var(--ra-cyber-aqua)' }}>
                          {facts.openGaps}
                        </div>
                        <div style={{ fontSize: '14px', color: 'var(--ra-text-secondary)', fontWeight: '600' }}>
                          Open Compliance Gaps
                        </div>
                        {facts.measured && (
                          <div style={{ fontSize: '12px', color: 'var(--ra-text-secondary)', marginTop: '4px' }}>
                            {[facts.criticalDetail, facts.evaluatedDetail].filter(Boolean).join(' · ')}
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Evidence Count */}
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '36px', fontWeight: '800', color: 'var(--ra-cyber-aqua)' }}>
                      {prismData.evidence_count ?? 0}
                    </div>
                    <div style={{ fontSize: '14px', color: 'var(--ra-text-secondary)', fontWeight: '600' }}>
                      Evidence Documents
                    </div>
                  </div>

                  {/* Assets */}
                  <div style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: '36px', fontWeight: '800', color: 'var(--ra-cyber-aqua)' }}>
                      {prismData.total_countermeasures ?? 0}
                    </div>
                    <div style={{ fontSize: '14px', color: 'var(--ra-text-secondary)', fontWeight: '600' }}>
                      Active Countermeasures
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--ra-text-muted)', marginTop: '2px' }}>
                      across {prismData.total_assets ?? 0} assets
                    </div>
                  </div>
                </div>

                {/* PDR Maturity */}
                {prismData.pdr_maturity && (
                  <div style={{ marginBottom: '24px' }}>
                    <div style={{ fontSize: '14px', fontWeight: '700', color: '#ccd6f6', marginBottom: '12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Protect / Detect / Respond Maturity
                    </div>
                    {[
                      { label: 'Protect', value: prismData.pdr_maturity.protect ?? prismData.pdr_maturity.Protect, style: {} },
                      { label: 'Detect', value: prismData.pdr_maturity.detect ?? prismData.pdr_maturity.Detect, style: { background: 'repeating-linear-gradient(135deg, var(--ra-cyber-aqua), var(--ra-cyber-aqua) 3px, #0066ff 3px, #0066ff 6px)' } },
                      { label: 'Respond', value: prismData.pdr_maturity.respond ?? prismData.pdr_maturity.Respond, style: { background: '#0066ff' } },
                    ].map(({ label, value, style: barStyle }) => (
                      <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                        <span style={{ width: '70px', fontSize: '13px', fontWeight: '600', color: '#a8b2d1' }}>{label}</span>
                        <div style={{ flex: 1, height: '12px', background: 'rgba(255, 255, 255, 0.08)', borderRadius: '6px', overflow: 'hidden' }}>
                          <div style={{
                            height: '100%', borderRadius: '6px', transition: 'width 0.5s',
                            width: `${Math.min(((value ?? 0) / 4) * 100, 100)}%`,
                            background: 'var(--ra-cyber-aqua)',
                            ...barStyle,
                          }} />
                        </div>
                        <span style={{ width: '35px', textAlign: 'right', fontSize: '13px', fontWeight: '700', color: 'var(--ra-text-primary)' }}>
                          {value != null ? `${value}/4` : '—'}
                        </span>
                      </div>
                    ))}
                  </div>
                )}

                {/* Top Gaps */}
                {prismData.top_gaps && prismData.top_gaps.length > 0 && (
                  <div>
                    <div style={{ fontSize: '14px', fontWeight: '700', color: '#ccd6f6', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      Priority Gaps
                    </div>
                    <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                      {prismData.top_gaps.map(boardReportGapLabel).filter((label): label is string => label !== null).slice(0, 3).map((label, i) => (
                        <span key={i} title={label} style={{
                          padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '600',
                          background: 'rgba(255, 68, 68, 0.08)', color: 'var(--ra-danger)', border: '1px solid rgba(255, 68, 68, 0.3)'
                        }}>
                          {label}
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Key Findings - executive and detailed */}
          {reportFormat !== 'presentation' && <div style={{ marginBottom: '48px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '32px'
            }}>
              <div style={{
                padding: '12px',
                background: 'linear-gradient(135deg, var(--ra-warning) 0%, var(--ra-warning) 100%)',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(249, 115, 22, 0.3)'
              }}>
                <AlertTriangle aria-hidden="true" size={24} color="white" />
              </div>
              <h2 style={{
                fontSize: '28px',
                fontWeight: '800',
                color: 'var(--ra-text-primary)',
                margin: 0,
                background: 'linear-gradient(135deg, var(--ra-warning) 0%, var(--ra-warning) 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>Key Findings</h2>
            </div>
            
            <div style={{
              background: 'linear-gradient(135deg, rgba(249, 115, 22, 0.05) 0%, rgba(234, 88, 12, 0.05) 100%)',
              border: '2px solid rgba(249, 115, 22, 0.2)',
              borderRadius: '20px',
              padding: '32px',
              boxShadow: '0 8px 32px rgba(249, 115, 22, 0.1)',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <div style={{
                position: 'absolute',
                top: '-50px',
                right: '-50px',
                width: '120px',
                height: '120px',
                background: 'linear-gradient(135deg, rgba(249, 115, 22, 0.1) 0%, rgba(234, 88, 12, 0.1) 100%)',
                borderRadius: '50%',
                pointerEvents: 'none'
              }} />
              
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px'
              }}>
                {simulationResults.boardSummary.keyFindings.map((finding, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '16px',
                    padding: '20px',
                    background: 'rgba(255, 255, 255, 0.04)',
                    borderRadius: '16px',
                    border: '1px solid rgba(249, 115, 22, 0.1)',
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 4px 12px rgba(249, 115, 22, 0.05)'
                  }}>
                    <div style={{
                      flexShrink: 0,
                      width: '32px',
                      height: '32px',
                      background: 'linear-gradient(135deg, var(--ra-warning) 0%, var(--ra-warning) 100%)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 12px rgba(249, 115, 22, 0.3)'
                    }}>
                      <span style={{
                        color: 'white',
                        fontWeight: '700',
                        fontSize: '14px'
                      }}>{index + 1}</span>
                    </div>
                    <p style={{
                      color: '#ccd6f6',
                      fontSize: '16px',
                      lineHeight: '1.6',
                      margin: 0,
                      fontWeight: '500'
                    }}>{finding}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>}

          {/* Investment Scenarios Comparison - detailed and presentation */}
          {reportFormat !== 'executive' && <div style={{ marginBottom: '48px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '32px'
            }}>
              <div style={{
                padding: '12px',
                background: 'linear-gradient(135deg, var(--ra-cyber-aqua) 0%, #0066ff 100%)',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
              }}>
                <BarChart3 aria-hidden="true" size={24} color="white" />
              </div>
              <h2 style={{
                fontSize: '28px',
                fontWeight: '800',
                color: 'var(--ra-text-primary)',
                margin: 0,
                background: 'linear-gradient(135deg, var(--ra-cyber-aqua) 0%, #0066ff 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>Investment Scenarios Analysis</h2>
            </div>
            
            <div style={{
              background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.05) 0%, rgba(29, 78, 216, 0.05) 100%)',
              border: '2px solid rgba(59, 130, 246, 0.2)',
              borderRadius: '20px',
              padding: '32px',
              boxShadow: '0 8px 32px rgba(59, 130, 246, 0.1)',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <div style={{
                position: 'absolute',
                top: '-50px',
                right: '-50px',
                width: '120px',
                height: '120px',
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1) 0%, rgba(29, 78, 216, 0.1) 100%)',
                borderRadius: '50%',
                pointerEvents: 'none'
              }} />
              
              <div style={{ overflowX: 'auto' }}>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(6, minmax(150px, 1fr))',
                  gap: '2px',
                  background: 'rgba(59, 130, 246, 0.1)',
                  borderRadius: '12px',
                  padding: '2px'
                }}>
                  {/* Header */}
                  <div style={{
                    padding: '16px 12px',
                    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(29, 78, 216, 0.2) 100%)',
                    fontWeight: '700',
                    fontSize: '14px',
                    color: 'var(--ra-cyber-aqua)',
                    textAlign: 'left',
                    borderRadius: '8px'
                  }}>Scenario</div>
                  <div style={{
                    padding: '16px 12px',
                    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(29, 78, 216, 0.2) 100%)',
                    fontWeight: '700',
                    fontSize: '14px',
                    color: 'var(--ra-cyber-aqua)',
                    textAlign: 'center',
                    borderRadius: '8px'
                  }}>Total Budget</div>
                  <div style={{
                    padding: '16px 12px',
                    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(29, 78, 216, 0.2) 100%)',
                    fontWeight: '700',
                    fontSize: '14px',
                    color: 'var(--ra-cyber-aqua)',
                    textAlign: 'center',
                    borderRadius: '8px'
                  }}>Risk Reduction</div>
                  <div style={{
                    padding: '16px 12px',
                    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(29, 78, 216, 0.2) 100%)',
                    fontWeight: '700',
                    fontSize: '14px',
                    color: 'var(--ra-cyber-aqua)',
                    textAlign: 'center',
                    borderRadius: '8px'
                  }}>Annual ROI</div>
                  <div style={{
                    padding: '16px 12px',
                    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(29, 78, 216, 0.2) 100%)',
                    fontWeight: '700',
                    fontSize: '14px',
                    color: 'var(--ra-cyber-aqua)',
                    textAlign: 'center',
                    borderRadius: '8px'
                  }}>Payback Period</div>
                  <div style={{
                    padding: '16px 12px',
                    background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.2) 0%, rgba(29, 78, 216, 0.2) 100%)',
                    fontWeight: '700',
                    fontSize: '14px',
                    color: 'var(--ra-cyber-aqua)',
                    textAlign: 'center',
                    borderRadius: '8px'
                  }}>Status</div>
                  
                  {/* Data Rows */}
                  {simulationResults.optimizedScenarios.map((scenario, index) => {
                    const isRecommended = scenario.scenarioName === simulationResults.recommendedScenario.scenarioName;
                    const rowBg = isRecommended ? 
                      'linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(22, 163, 74, 0.1) 100%)' :
                      'rgba(255, 255, 255, 0.04)';
                    
                    return [
                      <div key={`${index}-name`} style={{
                        padding: '16px 12px',
                        background: rowBg,
                        fontWeight: '600',
                        fontSize: '14px',
                        color: '#ccd6f6',
                        textAlign: 'left',
                        borderRadius: '8px',
                        border: isRecommended ? '1px solid rgba(34, 197, 94, 0.2)' : '1px solid rgba(59, 130, 246, 0.1)',
                      }}>{scenario.scenarioName}</div>,
                      <div key={`${index}-investment`} style={{
                        padding: '16px 12px',
                        background: rowBg,
                        fontSize: '14px',
                        color: '#ccd6f6',
                        textAlign: 'center',
                        borderRadius: '8px',
                        border: isRecommended ? '1px solid rgba(34, 197, 94, 0.2)' : '1px solid rgba(59, 130, 246, 0.1)',
                      }}>{formatCurrency(scenario.proposedBudget)}</div>,
                      <div key={`${index}-reduction`} style={{
                        padding: '16px 12px',
                        background: rowBg,
                        fontSize: '14px',
                        color: '#ccd6f6',
                        textAlign: 'center',
                        borderRadius: '8px',
                        border: isRecommended ? '1px solid rgba(34, 197, 94, 0.2)' : '1px solid rgba(59, 130, 246, 0.1)',
                      }}>{formatPercent(scenario.projectedRiskReduction)}</div>,
                      <div key={`${index}-roi`} style={{
                        padding: '16px 12px',
                        background: rowBg,
                        fontSize: '14px',
                        fontWeight: '600',
                        color: scenario.projectedROI > 1 ? 'var(--ra-success)' : scenario.projectedROI > 0.5 ? 'var(--ra-warning)' : 'var(--ra-danger)',
                        textAlign: 'center',
                        borderRadius: '8px',
                        border: isRecommended ? '1px solid rgba(34, 197, 94, 0.2)' : '1px solid rgba(59, 130, 246, 0.1)',
                      }}>{formatPercent(scenario.projectedROI)}</div>,
                      <div key={`${index}-payback`} style={{
                        padding: '16px 12px',
                        background: rowBg,
                        fontSize: '14px',
                        color: '#ccd6f6',
                        textAlign: 'center',
                        borderRadius: '8px',
                        border: isRecommended ? '1px solid rgba(34, 197, 94, 0.2)' : '1px solid rgba(59, 130, 246, 0.1)',
                      }}>{scenario.paybackPeriod.toFixed(1)} mo</div>,
                      <div key={`${index}-status`} style={{
                        padding: '16px 12px',
                        background: rowBg,
                        fontSize: '14px',
                        color: '#ccd6f6',
                        textAlign: 'center',
                        borderRadius: '8px',
                        border: isRecommended ? '1px solid rgba(34, 197, 94, 0.2)' : '1px solid rgba(59, 130, 246, 0.1)',
                      }}>
                        {isRecommended && (
                          <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            color: 'var(--ra-success)',
                            fontWeight: '600'
                          }}>
                            <CheckCircle aria-hidden="true" size={16} />
                            Recommended
                          </div>
                        )}
                      </div>
                    ];
                  })}
                </div>
              </div>
            </div>
          </div>}

          {/* Investment Allocation - detailed and presentation */}
          {reportFormat !== 'executive' && <div style={{ marginBottom: '48px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '32px'
            }}>
              <div style={{
                padding: '12px',
                background: 'linear-gradient(135deg, #0066ff 0%, var(--ra-cyber-aqua) 100%)',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)'
              }}>
                <PieChart aria-hidden="true" size={24} color="white" />
              </div>
              <h2 style={{
                fontSize: '28px',
                fontWeight: '800',
                color: 'var(--ra-text-primary)',
                margin: 0,
                background: 'linear-gradient(135deg, #0066ff 0%, var(--ra-cyber-aqua) 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>Recommended Investment Allocation</h2>
            </div>
            
            <div style={{
              background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.05) 0%, rgba(124, 58, 237, 0.05) 100%)',
              border: '2px solid rgba(139, 92, 246, 0.2)',
              borderRadius: '20px',
              padding: '32px',
              boxShadow: '0 8px 32px rgba(139, 92, 246, 0.1)',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <div style={{
                position: 'absolute',
                top: '-50px',
                right: '-50px',
                width: '120px',
                height: '120px',
                background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(124, 58, 237, 0.1) 100%)',
                borderRadius: '50%',
                pointerEvents: 'none'
              }} />
              
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: '20px'
              }}>
                {Object.entries(simulationResults.recommendedScenario.categoryAllocations)
                  .sort(([,a], [,b]) => b - a)
                  .map(([category, amount], index) => {
                    const percentage = amount / simulationResults.recommendedScenario.budgetIncrease;
                    const colors = [
                      { bg: 'linear-gradient(135deg, rgba(0, 255, 136, 0.1) 0%, rgba(0, 255, 136, 0.05) 100%)', border: 'var(--ra-success)', text: 'var(--ra-success)' },
                      { bg: 'linear-gradient(135deg, rgba(0, 217, 255, 0.1) 0%, rgba(0, 102, 255, 0.05) 100%)', border: 'var(--ra-cyber-aqua)', text: 'var(--ra-cyber-aqua)' },
                      { bg: 'linear-gradient(135deg, rgba(251, 146, 60, 0.1) 0%, rgba(234, 88, 12, 0.1) 100%)', border: '#fb923c', text: '#ea580c' },
                      { bg: 'linear-gradient(135deg, rgba(236, 72, 153, 0.1) 0%, rgba(219, 39, 119, 0.1) 100%)', border: '#ec4899', text: '#db2777' },
                      { bg: 'linear-gradient(135deg, rgba(14, 165, 233, 0.1) 0%, rgba(2, 132, 199, 0.1) 100%)', border: '#0ea5e9', text: '#0284c7' },
                      { bg: 'linear-gradient(135deg, rgba(168, 85, 247, 0.1) 0%, rgba(147, 51, 234, 0.1) 100%)', border: '#a855f7', text: '#9333ea' }
                    ];
                    const colorSet = colors[index % colors.length];
                    
                    return (
                      <div key={category} style={{
                        background: colorSet.bg,
                        border: `2px solid ${colorSet.border}30`,
                        borderRadius: '16px',
                        padding: '24px',
                        position: 'relative',
                        boxShadow: `0 4px 16px ${colorSet.border}20`
                      }}>
                        <div style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          marginBottom: '16px'
                        }}>
                          <h3 style={{
                            fontSize: '18px',
                            fontWeight: '700',
                            color: 'var(--ra-text-primary)',
                            margin: 0,
                            textTransform: 'capitalize'
                          }}>
                            {category.replace('_', ' ')}
                          </h3>
                          <div style={{
                            background: `${colorSet.border}20`,
                            padding: '4px 12px',
                            borderRadius: '20px',
                            border: `1px solid ${colorSet.border}`,
                            fontSize: '12px',
                            fontWeight: '600',
                            color: colorSet.text
                          }}>
                            {formatPercent(percentage)}
                          </div>
                        </div>
                        
                        <div style={{
                          background: 'rgba(255, 255, 255, 0.04)',
                          padding: '16px',
                          borderRadius: '12px',
                          border: `1px solid ${colorSet.border}20`,
                          backdropFilter: 'blur(10px)'
                        }}>
                          <div style={{
                            fontSize: '28px',
                            fontWeight: '800',
                            color: colorSet.text,
                            marginBottom: '4px'
                          }}>
                            {formatCurrency(amount)}
                          </div>
                          <div style={{
                            fontSize: '14px',
                            color: 'var(--ra-text-secondary)',
                            fontWeight: '500'
                          }}>
                            Investment Amount
                          </div>
                        </div>
                        
                        {/* Progress bar */}
                        <div style={{
                          marginTop: '16px',
                          background: 'rgba(148, 163, 184, 0.2)',
                          borderRadius: '8px',
                          height: '8px',
                          overflow: 'hidden'
                        }}>
                          <div style={{
                            background: colorSet.border,
                            height: '100%',
                            borderRadius: '8px',
                            width: `${percentage * 100}%`,
                            transition: 'width 1s ease-in-out'
                          }} />
                        </div>
                      </div>
                    );
                  })}
              </div>
            </div>
          </div>}

          {/* Strategic Recommendations - detailed only */}
          {reportFormat === 'detailed' && <div style={{ marginBottom: '48px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '32px'
            }}>
              <div style={{
                padding: '12px',
                background: 'linear-gradient(135deg, var(--ra-success) 0%, var(--ra-success) 100%)',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(34, 197, 94, 0.3)'
              }}>
                <Target aria-hidden="true" size={24} color="white" />
              </div>
              <h2 style={{
                fontSize: '28px',
                fontWeight: '800',
                color: 'var(--ra-text-primary)',
                margin: 0,
                background: 'linear-gradient(135deg, var(--ra-success) 0%, var(--ra-success) 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>Strategic Recommendations</h2>
            </div>
            
            <div style={{
              background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.05) 0%, rgba(22, 163, 74, 0.05) 100%)',
              border: '2px solid rgba(34, 197, 94, 0.2)',
              borderRadius: '20px',
              padding: '32px',
              boxShadow: '0 8px 32px rgba(34, 197, 94, 0.1)',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <div style={{
                position: 'absolute',
                top: '-50px',
                right: '-50px',
                width: '120px',
                height: '120px',
                background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(22, 163, 74, 0.1) 100%)',
                borderRadius: '50%',
                pointerEvents: 'none'
              }} />
              
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px'
              }}>
                {simulationResults.boardSummary.recommendations.map((rec, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '16px',
                    padding: '20px',
                    background: 'rgba(255, 255, 255, 0.04)',
                    borderRadius: '16px',
                    border: '1px solid rgba(34, 197, 94, 0.1)',
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 4px 12px rgba(34, 197, 94, 0.05)'
                  }}>
                    <div style={{
                      flexShrink: 0,
                      width: '32px',
                      height: '32px',
                      background: 'linear-gradient(135deg, var(--ra-success) 0%, var(--ra-success) 100%)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 12px rgba(34, 197, 94, 0.3)'
                    }}>
                      <CheckCircle aria-hidden="true" size={18} color="white" />
                    </div>
                    <p style={{
                      color: '#ccd6f6',
                      fontSize: '16px',
                      lineHeight: '1.6',
                      margin: 0,
                      fontWeight: '500'
                    }}>{rec}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>}

          {/* Implementation Priorities - detailed only */}
          {reportFormat === 'detailed' && <div style={{ marginBottom: '48px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '32px'
            }}>
              <div style={{
                padding: '12px',
                background: 'linear-gradient(135deg, #0066ff 0%, var(--ra-cyber-aqua) 100%)',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(139, 92, 246, 0.3)'
              }}>
                <Calendar aria-hidden="true" size={24} color="white" />
              </div>
              <h2 style={{
                fontSize: '28px',
                fontWeight: '800',
                color: 'var(--ra-text-primary)',
                margin: 0,
                background: 'linear-gradient(135deg, #0066ff 0%, var(--ra-cyber-aqua) 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>Implementation Priorities</h2>
            </div>
            
            <div style={{
              background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.05) 0%, rgba(124, 58, 237, 0.05) 100%)',
              border: '2px solid rgba(139, 92, 246, 0.2)',
              borderRadius: '20px',
              padding: '32px',
              boxShadow: '0 8px 32px rgba(139, 92, 246, 0.1)',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <div style={{
                position: 'absolute',
                top: '-50px',
                right: '-50px',
                width: '120px',
                height: '120px',
                background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(124, 58, 237, 0.1) 100%)',
                borderRadius: '50%',
                pointerEvents: 'none'
              }} />
              
              <div style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '20px'
              }}>
                {simulationResults.boardSummary.investmentPriority.slice(0, 3).map((priority, index) => (
                  <div key={index} style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '20px',
                    padding: '24px',
                    background: 'rgba(255, 255, 255, 0.04)',
                    borderRadius: '16px',
                    border: '1px solid rgba(139, 92, 246, 0.1)',
                    backdropFilter: 'blur(10px)',
                    boxShadow: '0 4px 12px rgba(139, 92, 246, 0.05)',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease'
                  }}>
                    <div style={{
                      flexShrink: 0,
                      width: '40px',
                      height: '40px',
                      background: 'linear-gradient(135deg, #0066ff 0%, var(--ra-cyber-aqua) 100%)',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 6px 16px rgba(139, 92, 246, 0.3)',
                      border: '2px solid white'
                    }}>
                      <span style={{
                        color: 'white',
                        fontWeight: '800',
                        fontSize: '16px'
                      }}>{index + 1}</span>
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{
                        fontWeight: '700',
                        fontSize: '18px',
                        color: 'var(--ra-cyber-aqua)',
                        marginBottom: '8px',
                        lineHeight: '1.4'
                      }}>{priority}</div>
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <div style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: 'var(--ra-cyber-aqua)',
                          background: 'rgba(139, 92, 246, 0.1)',
                          padding: '4px 12px',
                          borderRadius: '20px',
                          border: '1px solid rgba(139, 92, 246, 0.2)'
                        }}>
                          Priority {index + 1}
                        </div>
                        <div style={{
                          fontSize: '14px',
                          color: 'var(--ra-text-secondary)',
                          fontWeight: '500'
                        }}>
                          {index === 0 ? 'Immediate (0-3 months)' : 
                           index === 1 ? 'Short-term (3-6 months)' : 
                           'Medium-term (6-12 months)'}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>}

          {/* Risk Tolerance Assessment */}
          <div style={{ marginBottom: '48px' }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              marginBottom: '32px'
            }}>
              <div style={{
                padding: '12px',
                background: 'linear-gradient(135deg, var(--ra-cyber-aqua) 0%, #0066ff 100%)',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(6, 182, 212, 0.3)'
              }}>
                <Shield aria-hidden="true" size={24} color="white" />
              </div>
              <h2 style={{
                fontSize: '28px',
                fontWeight: '800',
                color: 'var(--ra-text-primary)',
                margin: 0,
                background: 'linear-gradient(135deg, var(--ra-cyber-aqua) 0%, #0066ff 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent'
              }}>Risk Tolerance Assessment</h2>
            </div>
            
            <div style={{
              background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.05) 0%, rgba(8, 145, 178, 0.05) 100%)',
              border: '2px solid rgba(6, 182, 212, 0.2)',
              borderRadius: '20px',
              padding: '32px',
              boxShadow: '0 8px 32px rgba(6, 182, 212, 0.1)',
              position: 'relative',
              overflow: 'hidden'
            }}>
              <div style={{
                position: 'absolute',
                top: '-50px',
                right: '-50px',
                width: '120px',
                height: '120px',
                background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.1) 0%, rgba(8, 145, 178, 0.1) 100%)',
                borderRadius: '50%',
                pointerEvents: 'none'
              }} />
              
              <div style={{
                background: 'rgba(255, 255, 255, 0.04)',
                padding: '28px',
                borderRadius: '16px',
                border: '1px solid rgba(6, 182, 212, 0.1)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 4px 12px rgba(6, 182, 212, 0.05)'
              }}>
                <div style={{
                  fontSize: '18px',
                  lineHeight: '1.6',
                  color: '#ccd6f6',
                  marginBottom: '16px'
                }}>
                  Based on the current risk exposure analysis, the organization's risk posture is classified as{' '}
                  <span style={{
                    fontWeight: '800',
                    fontSize: '20px',
                    textTransform: 'capitalize',
                    color: simulationResults.boardSummary.riskTolerance === 'aggressive' ? 'var(--ra-danger)' :
                           simulationResults.boardSummary.riskTolerance === 'moderate' ? 'var(--ra-warning)' : 'var(--ra-success)',
                    background: simulationResults.boardSummary.riskTolerance === 'aggressive' ? 'rgba(220, 38, 38, 0.1)' :
                               simulationResults.boardSummary.riskTolerance === 'moderate' ? 'rgba(234, 88, 12, 0.1)' : 'rgba(22, 163, 74, 0.1)',
                    padding: '4px 12px',
                    borderRadius: '8px',
                    border: `2px solid ${
                      simulationResults.boardSummary.riskTolerance === 'aggressive' ? 'rgba(220, 38, 38, 0.2)' :
                      simulationResults.boardSummary.riskTolerance === 'moderate' ? 'rgba(234, 88, 12, 0.2)' : 'rgba(22, 163, 74, 0.2)'
                    }`
                  }}>
                    {simulationResults.boardSummary.riskTolerance}
                  </span>
                  .
                </div>
                <div style={{
                  fontSize: '16px',
                  color: 'var(--ra-text-secondary)',
                  fontStyle: 'italic'
                }}>
                  This assessment is based on the current revenue exposure percentage and industry benchmarks for {companyData.industry} organizations.
                </div>
              </div>
            </div>
          </div>

          {/* Report Footer */}
          <div style={{
            borderTop: '2px solid rgba(6, 182, 212, 0.2)',
            paddingTop: '32px',
            textAlign: 'center',
            background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.05) 0%, rgba(8, 145, 178, 0.05) 100%)',
            borderRadius: '20px',
            padding: '32px',
            marginTop: '24px'
          }}>
            <div style={{
              fontSize: '14px',
              color: 'var(--ra-text-secondary)',
              marginBottom: '8px',
              fontWeight: '500'
            }}>
              Financial Impact Analysis using {simulationResults.currentState.iterations.toLocaleString()} Monte Carlo iterations
            </div>
            <div style={{
              fontSize: '14px',
              color: 'var(--ra-text-secondary)',
              marginBottom: '16px',
              fontWeight: '500'
            }}>
              Analysis performed on {currentDate} • Foundations Risk Aperture Platform
            </div>
            <div style={{
              fontSize: '16px',
              fontWeight: '800',
              color: 'var(--ra-cyber-aqua)',
              background: 'rgba(255, 255, 255, 0.04)',
              padding: '12px 24px',
              borderRadius: '12px',
              border: '2px solid rgba(6, 182, 212, 0.2)',
              display: 'inline-block',
              boxShadow: '0 4px 12px rgba(6, 182, 212, 0.1)'
            }}>
              CONFIDENTIAL - For Board and Executive Review Only
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BoardReportGenerator;